import type { HapticPattern } from "../types";

const MAX_DURATION = 1000;
const PWM_CYCLE = 20;

/**
 * Android: navigator.vibrate() with PWM intensity simulation.
 * iOS Safari 17.4+: hidden <input switch> toggle driven by requestAnimationFrame.
 *
 * Both drivers can be active simultaneously — iOS devices ignore vibrate(),
 * Android ignores the switch element (it only fires haptics in WebKit on iOS).
 */
export class VibrationDriver {
  private switchLabel: HTMLLabelElement | null = null;
  private rafId: number | null = null;
  private cancelled = false;

  private static readonly canVibrate =
    typeof navigator !== "undefined" && typeof navigator.vibrate === "function";

  private static readonly isIOS =
    typeof navigator !== "undefined" && /iP(hone|ad|od)/.test(navigator.userAgent);

  public get isSupported(): boolean {
    return VibrationDriver.canVibrate || VibrationDriver.isIOS;
  }

  public initialize(): void {
    if (VibrationDriver.isIOS) {
      this.initSwitchDOM();
    }
  }

  public play(pattern: HapticPattern): void {
    this.cancel();
    this.cancelled = false;

    const safe = pattern.map((v) => ({
      duration: Math.min(Math.max(0, v.duration), MAX_DURATION),
      intensity: Math.max(0, Math.min(1, v.intensity ?? 0.5)),
      delay: Math.max(0, v.delay ?? 0),
    }));

    if (VibrationDriver.canVibrate) {
      navigator.vibrate(buildVibratePattern(safe));
    }

    if (VibrationDriver.isIOS && this.switchLabel) {
      this.runSwitchPattern(safe);
    }
  }

  public cancel(): void {
    this.cancelled = true;

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    if (VibrationDriver.canVibrate) {
      navigator.vibrate(0);
    }
  }

  public destroy(): void {
    this.cancel();
    this.switchLabel?.remove();
    this.switchLabel = null;
  }

  // ── iOS switch DOM ────────────────────────────────────────────────────

  private initSwitchDOM(): void {
    const label = document.createElement("label");
    label.style.cssText =
      "position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;overflow:hidden;top:0;left:0;";

    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.setAttribute("switch", "");
    cb.style.cssText = "all:initial;appearance:auto;";
    cb.tabIndex = -1;

    label.appendChild(cb);
    document.body.appendChild(label);

    this.switchLabel = label;
  }

  private runSwitchPattern(
    vibrations: { duration: number; intensity: number; delay: number }[]
  ): void {
    const phases: { start: number; end: number; isOn: boolean; interval: number }[] = [];
    let cumulative = 0;

    for (const vib of vibrations) {
      if (vib.delay > 0) {
        phases.push({ start: cumulative, end: cumulative + vib.delay, isOn: false, interval: 0 });
        cumulative += vib.delay;
      }

      // Higher intensity → more frequent clicks → stronger perceived haptic
      const interval = Math.max(16, Math.round(16 + (1 - vib.intensity) * 184));
      phases.push({ start: cumulative, end: cumulative + vib.duration, isOn: true, interval });
      cumulative += vib.duration;
    }

    const totalMs = cumulative;
    let startTime = 0;
    let lastClick = -Infinity;

    // First click synchronously — stays within user gesture window
    const firstOn = phases.find((p) => p.isOn && p.start === 0);
    if (firstOn) {
      this.switchLabel!.click();
      lastClick = performance.now();
    }

    const loop = (t: number) => {
      if (startTime === 0) startTime = t;
      const elapsed = t - startTime;

      if (elapsed >= totalMs || this.cancelled) {
        this.rafId = null;
        return;
      }

      const phase = phases.find((p) => elapsed >= p.start && elapsed < p.end);
      if (phase?.isOn && t - lastClick >= phase.interval) {
        this.switchLabel!.click();
        lastClick = t;
      }

      this.rafId = requestAnimationFrame(loop);
    };

    this.rafId = requestAnimationFrame(loop);
  }
}

// ── Android PWM pattern builder ───────────────────────────────────────────

type SafeVibration = { duration: number; intensity: number; delay: number };

function buildVibratePattern(vibrations: SafeVibration[]): number[] {
  const result: number[] = [];

  for (const vib of vibrations) {
    if (vib.delay > 0) {
      appendGap(result, vib.delay);
    }

    if (vib.intensity >= 1) {
      result.push(vib.duration);
    } else if (vib.intensity <= 0) {
      appendGap(result, vib.duration);
    } else {
      appendPWM(result, vib.duration, vib.intensity);
    }
  }

  return result;
}

function appendGap(result: number[], ms: number): void {
  if (result.length > 0 && result.length % 2 === 0) {
    result[result.length - 1] += ms;
  } else {
    if (result.length === 0) result.push(0);
    result.push(ms);
  }
}

function appendPWM(result: number[], duration: number, intensity: number): void {
  const onTime = Math.max(1, Math.round(PWM_CYCLE * intensity));
  const offTime = PWM_CYCLE - onTime;
  let remaining = duration;

  while (remaining >= PWM_CYCLE) {
    result.push(onTime, offTime);
    remaining -= PWM_CYCLE;
  }

  if (remaining > 0) {
    const remOn = Math.max(1, Math.round(remaining * intensity));
    result.push(remOn);
    const remOff = remaining - remOn;
    if (remOff > 0) result.push(remOff);
  }
}
